
		import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "dreamland/jsx-runtime";
export const title = "iOS Sideloading: The Rundown";
export const tags = ["News", "Apple", "Opinion"];
export const description = "Explains Apple’s iOS sideloading changes for the EU, how they work, and implications for developers and users.";
function _createMdxContent(props) {
  const _components = {
    a: "a",
    blockquote: "blockquote",
    code: "code",
    h2: "h2",
    li: "li",
    p: "p",
    strong: "strong",
    ul: "ul",
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsxs(_components.p, {
      children: ["Apple ", _jsx(_components.a, {
        href: "https://www.apple.com/newsroom/2024/01/apple-announces-changes-to-ios-safari-and-the-app-store-in-the-european-union/",
        children: "just announced"
      }), " sideloading for the EU on iOS 17.4 and above. What does this mean for you?"]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Note:"
        }), " Scroll to the bottom of the article for a TL;DR."]
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Updates"
    }), "\n", _jsx(_components.p, {
      children: "Recently, Apple has added the capability to install apps directly from a developer's website. However, the review requirements discussed below still apply."
    }), "\n", _jsx(_components.h2, {
      children: "Access control"
    }), "\n", _jsxs(_components.p, {
      children: [_jsx(_components.strong, {
        children: "NEW:"
      }), " iOS appears to use a new daemon, ", _jsx(_components.code, {
        children: "countryd"
      }), ", to determine eligibility. It checks various device factors, such as Wi-Fi region, SIM card, and more."]
    }), "\n", _jsxs(_components.p, {
      children: ["In iOS 17.3, a new MobileGestalt key called ", _jsx(_components.code, {
        children: "DeviceSupportsEUCapabilities"
      }), " was added, which may have something to do with sideloading. If somehow a sandbox escape is released for 17.4, it might be possible to enable this even if you don't live in the EU. However, it may not be worth it. Let's find out."]
    }), "\n", _jsx(_components.h2, {
      children: "Becoming a Marketplace"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: ["All quotes in this section come from ", _jsx(_components.a, {
          href: "https://developer.apple.com/support/alternative-app-marketplace-in-the-eu/",
          children: "this article."
        })]
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["First of all, the changes aren't \"sideloading\" per se. Instead, Apple is allowing ", _jsx(_components.strong, {
        children: "registered developers"
      }), " (the ones who pay a $100 fee yearly) to create an \"alternative app marketplace\". I'll let Apple themselves explain how someone can do that."]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "If you’re interested in becoming a marketplace developer in the EU, the Account Holder of your Apple Developer Program membership will first need to agree to the Alternative Terms Addendum for Apps in the EU. Once they’ve agreed, they can submit a request for the entitlement."
      }), "\n"]
    }), "\n", _jsxs(_components.p, {
      children: ["Additionally, you must \"provide Apple a stand-by letter of credit from an A-rated financial Institution of ", _jsx(_components.strong, {
        children: "€1,000,000"
      }), "\". This makes it impossible for smaller companies (let alone individuals) to open their own app stores, and essentially crushes any hopes of true sideloading by the end user."]
    }), "\n", _jsx(_components.h2, {
      children: "Codesigning"
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: ["All quotes in this section come from ", _jsx(_components.a, {
          href: "https://developer.apple.com/support/dma-and-apps-in-the-eu/#ios-app-eu",
          children: "this article."
        })]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "As you may know, all binaries that run on iOS require a valid code signature. Let's see how this works for the new sideloading mechanism."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Developers can submit a single binary and will be able to choose alternative distribution options in App Store Connect."
      }), "\n"]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsx(_components.p, {
        children: "Apple will encrypt and sign all iOS apps intended for alternative distribution to help protect developers’ intellectual property and ensure that users get apps from known parties."
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "These two quotes from Apple signify that you will still have to submit apps to Apple for signing if you want them to run on iOS."
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Functionality"
        }), " — Binaries must be reviewable, free of serious bugs or crashes, and compatible with the current version of iOS. They cannot manipulate software or hardware in ways that negatively impact the user experience."]
      }), "\n"]
    }), "\n", _jsxs(_components.blockquote, {
      children: ["\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Security"
        }), " — Apps cannot enable distribution of malware or of suspicious or unwanted software. They cannot download executable code, read outside of the container, or direct users to lower the security on their system or device. Also, apps must provide transparency and allow user consent to enable any party to access the system or device, or reconfigure the system or other software."]
      }), "\n"]
    }), "\n", _jsx(_components.p, {
      children: "These two points mean that it will be impossible to install jailbreaks (or jailed tools) through this new \"sideloading\" mechanism."
    }), "\n", _jsx(_components.h2, {
      children: "Conclusion"
    }), "\n", _jsxs(_components.p, {
      children: ["Apple's recent announcement of allowing third-party app stores for customers in the EU is not much more than a case of malicious compliance. They did the bare minimum to comply with the law, and it shows. If you want a better explanation, I highly recommend ", _jsx(_components.a, {
        href: "https://youtu.be/_6dbNzFD0zM",
        children: "this video"
      }), "."]
    }), "\n", _jsx(_components.h2, {
      children: "TL;DR"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsx(_components.li, {
        children: "The new \"sideloading\" in iOS 17.4 sucks."
      }), "\n", _jsx(_components.li, {
        children: "Developers have to pay Apple not only $99 a year for the Dev Program, but also €1,000,000 so Apple can \"trust\" them."
      }), "\n", _jsx(_components.li, {
        children: "All apps still need to be reviewed by Apple."
      }), "\n", _jsx(_components.li, {
        children: "It will be impossible to install jailbreaks/jailed tools with this new sideloading mechanism."
      }), "\n"]
    }), "\n", _jsx(_components.h2, {
      children: "Updates"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["2024-01-26 (shortly after initial publish)", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsx(_components.li, {
            children: "Add conclusion"
          }), "\n"]
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["2024-04-23", "\n", _jsxs(_components.ul, {
          children: ["\n", _jsxs(_components.li, {
            children: ["Fix wording, clarify web installation and ", _jsx(_components.code, {
              children: "countryd"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n"]
    })]
  });
}
export function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = props.components || ({});
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}


		export default function Page() {
			const {wrapper: MDXLayout} = this.components || ({});
			return (
				MDXLayout 
					? _jsx(MDXLayout, { children: [_createMdxContent(this)], ...this })
					: _createMdxContent(this)
			)
		}
	